import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getPayments, getPaymentsForAllOrganisations, getPaymentMethods } from '../services/payment.service';
import { toPaymentMethodOptions } from '../utils/paymentMethods';
import PaymentsTable from "../components/payments/PaymentsTable";
import ExportPaymentsDialog from "../components/payments/ExportPaymentsDialog";
import { useOrganisation } from '../hooks/useOrganisation';
import { useToast } from "../components/ui/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { isAllOrganisationsSelected } from '../utils/organisation';
import { Download } from 'lucide-react';
import { Button } from '../components/ui/button';

const TAB_PENDING = 'pending';
const TAB_APPROVED = 'approved-rejected';

const PaymentsPage = () => {
    const initialPage = 1; // Default page
    const initialLimit = 10; // Default limit
    const [searchParams, setSearchParams] = useSearchParams();
    const initialMeta = {         
      "count": 0,
      "pageInfo": {
          "page": initialPage,
          "limit": initialLimit,
          "hasNextPage": false,
          "totalCount": 0,
          "lastPage": initialPage
      }}
    const [pendingPayments, setPendingPayments] = useState({data: [], meta: initialMeta});
    const [approvedPayments, setApprovedPayments] = useState({data: [], meta: initialMeta});
    const [pendingLoading, setPendingLoading] = useState(false);
    const [approvedLoading, setApprovedLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_PENDING);
    const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
    const { user } = useAuth();
    const { organisation } = useOrganisation();
    const timezone = organisation?.timezone || null;
    const { toast } = useToast();

    // The aggregate "All Organisations" view is driven by the header switcher
    // selection rather than a per-page toggle.
    const viewAllOrgs = isAllOrganisationsSelected(user);

    // Tracks the latest aggregate request per tab so responses from requests
    // superseded by organisation, view, or filter changes can be discarded.
    // Keyed per tab because the two tabs fetch concurrently and must not cancel
    // each other.
    const allOrgsRequestRef = useRef({});

    // Parse URL parameters with defaults
    const urlParams = useMemo(() => ({
        pendingPage: Number.parseInt(searchParams.get('pendingPage'), 10) || 1,
        approvedPage: Number.parseInt(searchParams.get('approvedPage'), 10) || 1,
        approvedStatus: searchParams.get('approvedStatus') || 'Approved,Rejected',
    }), [searchParams]);

    // Update URL parameters
    const updateUrlParams = useCallback((updates) => {
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            
            Object.entries(updates).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    newParams.set(key, value.toString());
                } else {
                    newParams.delete(key);
                }
            });
            
            return newParams;
        }, { replace: true });
    }, [setSearchParams]);

    // Handle API Response state updates and error messages
    const handleApiResponse = useCallback((response, setStateFunction) => {
      if (response?.data?.success && Array.isArray(response.data.data)) {
          const paymentData = response.data.data || [];
          const paymentMeta = response.data.meta || {};
          setStateFunction({data: paymentData.reverse(), meta: paymentMeta});
      } else if (response && response.data.error) {
          toast({
              title: response.data.error.name || 'Error',
              description: response.data.error.message || 'An error occurred while fetching payments',
              variant: "destructive",
          });
      } else {
          toast({
              title: response.status ? `Error ${response.status}` : 'Error',
              description: response.statusText || 'An unexpected error occurred',
              variant: "destructive",
          });
      }
    }, [toast]);

    // Maps a payment status to the state setters for the tab it belongs to.
    const getTabTargets = useCallback((paymentType) => {
        if (paymentType === 'Pending') {
            return { setLoading: setPendingLoading, setPayments: setPendingPayments, cacheKey: 'pending' };
        }
        return { setLoading: setApprovedLoading, setPayments: setApprovedPayments, cacheKey: 'approved' };
    }, []);

    // Fetch one server-paginated page across organisations via the aggregate API.
    const fetchPaymentsForAllOrgs = useCallback(async (paymentType, page, limit, filtering) => {
        const { setLoading, setPayments, cacheKey } = getTabTargets(paymentType);
        const { organisationIds, ...apiFiltering } = filtering || {};
        const pageSize = limit || initialLimit;

        // Claim a new request generation for this tab; any older in-flight
        // request for the same tab is now stale and its response is ignored.
        const requestId = (allOrgsRequestRef.current[cacheKey] || 0) + 1;
        allOrgsRequestRef.current[cacheKey] = requestId;
        const isCurrent = () => allOrgsRequestRef.current[cacheKey] === requestId;

        // Organisation filter narrows which IDs are sent to the aggregate API.
        const organisationsToFetch = organisationIds?.length
            ? (user.organisations || []).filter((org) =>
                organisationIds.some((id) => String(id) === String(org.id))
              )
            : (user.organisations || []);

        if (organisationsToFetch.length === 0) {
            setPayments({
                data: [],
                meta: {
                    count: 0,
                    pageInfo: {
                        page: 1,
                        limit: pageSize,
                        hasNextPage: false,
                        totalCount: 0,
                        lastPage: 1,
                    },
                },
            });
            return;
        }

        setLoading(true);
        try {
            const response = await getPaymentsForAllOrganisations(
                user.token,
                organisationsToFetch,
                paymentType,
                page,
                pageSize,
                apiFiltering
            );

            if (!isCurrent()) {
                return;
            }

            handleApiResponse(response, setPayments);
        } catch (error) {
            if (!isCurrent()) {
                return;
            }
            toast({
                title: 'Error',
                description: error.message || 'An unexpected error occurred',
                variant: 'destructive',
            });
        } finally {
            if (isCurrent()) {
                setLoading(false);
            }
        }
    }, [user, getTabTargets, toast, handleApiResponse]);

    //Fetch Individual Payment Type
    const fetchPaymentsByType = useCallback(async (paymentType, page, limit, filtering) => {
        if (viewAllOrgs) {
            await fetchPaymentsForAllOrgs(paymentType, page, limit || initialLimit, filtering);
            return;
        }

        if (paymentType === 'Pending') {
            setPendingLoading(true);
        } else if (paymentType === 'Approved' || paymentType === 'Rejected' || paymentType === 'Approved,Rejected') {
            setApprovedLoading(true);
        }
      try {
          const response = await getPayments(user.token, user.organisationId, paymentType, page, limit, filtering);
          if (paymentType === 'Pending') {
              handleApiResponse(response, setPendingPayments);
          } else if (paymentType === 'Approved' || paymentType === 'Rejected' || paymentType === 'Approved,Rejected') {
              handleApiResponse(response, setApprovedPayments);
          }
      } catch (error) {
          toast({
              title: 'Error',
              description: error.message || 'An unexpected error occurred',
              variant: "destructive",
          });
      } finally {
        if (paymentType === 'Pending') {
            setPendingLoading(false);
        } else if (paymentType === 'Approved' || paymentType === 'Rejected' || paymentType === 'Approved,Rejected') {
            setApprovedLoading(false);
        }
      }
    }, [user, handleApiResponse, toast, viewAllOrgs, fetchPaymentsForAllOrgs]);

    //Fetch All Payments on load using URL params for initial page.
    //Re-runs when the org set changes or the "all organisations" toggle flips.
    useEffect(() => {
      const fetchAllPayments = async() => {
        setApprovedLoading(true);
        setPendingLoading(true);
        try {
            await Promise.all([
                fetchPaymentsByType('Pending', urlParams.pendingPage, initialLimit),
                fetchPaymentsByType(urlParams.approvedStatus, urlParams.approvedPage, initialLimit)
            ]);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch payments',
                variant: "destructive",
            });
        } finally {
            setApprovedLoading(false);
            setPendingLoading(false);
        }
      };

      fetchAllPayments();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, viewAllOrgs]);

    // There is no aggregate methods endpoint, so the "All Organisations" view merges each organisation's list.
    useEffect(() => {
        let cancelled = false;
        const organisationIds = viewAllOrgs
            ? (user.organisations || []).map((org) => org.id).filter((id) => id != null)
            : [user.organisationId];

        const loadPaymentMethods = async () => {
            const results = await Promise.allSettled(
                organisationIds.map((organisationId) => getPaymentMethods(user.token, organisationId))
            );
            if (cancelled) return;

            const methods = results.flatMap((result) =>
                result.status === 'fulfilled'
                && result.value?.data?.success
                && Array.isArray(result.value.data.data)
                    ? result.value.data.data
                    : []
            );
            setPaymentMethodOptions(toPaymentMethodOptions(methods));

            if (results.some((result) => result.status === 'rejected' || !result.value?.data?.success)) {
                toast({
                    title: 'Error',
                    description: 'Some payment methods could not be loaded for filtering',
                    variant: 'destructive',
                });
            }
        };

        loadPaymentMethods();
        return () => {
            cancelled = true;
        };
    }, [user.token, user.organisationId, user.organisations, viewAllOrgs, toast]);

    //Handle Query Change for Pagination and Filtering
    const handleQueryChange = useCallback((page, paymentType, limit, filtering) => {
      if (paymentType === 'Pending') {
        updateUrlParams({ pendingPage: page });
        fetchPaymentsByType("Pending", page, limit, filtering);
      } else if (paymentType === 'Approved') {
        updateUrlParams({ approvedPage: page, approvedStatus: 'Approved' });
        fetchPaymentsByType("Approved", page, limit, filtering);
      } else if (paymentType === 'Rejected') {
        updateUrlParams({ approvedPage: page, approvedStatus: 'Rejected' });
        fetchPaymentsByType("Rejected", page, limit, filtering);
      } else if (paymentType === 'Approved,Rejected') {
        updateUrlParams({ approvedPage: page, approvedStatus: 'Approved,Rejected' });
        fetchPaymentsByType("Approved,Rejected", page, limit, filtering);
      }
    }, [fetchPaymentsByType, updateUrlParams]);

    return (
        <div className="App">
            <div className="w-full max-w-9xl mx-auto relative">
                <div className="absolute right-0 top-0 z-10 flex h-[42px] items-center">
                    <ExportPaymentsDialog
                        token={user.token}
                        organisationId={user.organisationId}
                        organisations={user.organisations}
                        viewAllOrgs={viewAllOrgs}
                        timezone={timezone}
                        organisationName={user.organisationName}
                    >
                        <Button variant="ghost" size="sm" className="gap-1.5 text-slate-500 font-medium hover:text-slate-700">
                            <Download size={14} />
                            Export
                        </Button>
                    </ExportPaymentsDialog>
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="pr-28">
                        <TabsTrigger value={TAB_PENDING}>Pending</TabsTrigger>
                        <TabsTrigger value={TAB_APPROVED}>Approved / Rejected</TabsTrigger>
                    </TabsList>

                    <TabsContent value={TAB_PENDING}>
                        {/* Remount whenever the selected organisation changes
                            (including to/from "All Organisations") so the table's
                            internal filter state resets to match the unfiltered
                            refetch that switch triggers. */}
                        <PaymentsTable
                            key={`pending-${user.organisationId}`}
                            loading={pendingLoading}
                            payments={pendingPayments}
                            title="Pending Payments"
                            tableType="Pending"
                            onQueryChange={handleQueryChange}
                            timezone={timezone}
                            showOrganisation={viewAllOrgs}
                            organisations={user.organisations}
                            paymentMethods={paymentMethodOptions}
                        />
                    </TabsContent>

                    <TabsContent value={TAB_APPROVED}>
                        <PaymentsTable
                            key={`approved-${user.organisationId}`}
                            loading={approvedLoading}
                            payments={approvedPayments}
                            title="Approved / Rejected Payments"
                            tableType="Approved"
                            onQueryChange={handleQueryChange}
                            initialStatus={urlParams.approvedStatus}
                            timezone={timezone}
                            showOrganisation={viewAllOrgs}
                            organisations={user.organisations}
                            paymentMethods={paymentMethodOptions}
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default PaymentsPage;
